﻿/**
 * dgFav - Distribution Gauthier Favorites API v4.0
 * ═══════════════════════════════════════════════
 * Single source of truth for favorites + custom folders (multi-tag).
 * Works for guests (localStorage only) and logged-in users (+ Shopify App Proxy sync).
 *
 * NEW v4: Customer-defined folders. A favorite handle can belong to 0..N folders.
 *   Folders are pure tags ; the master `handles` list remains authoritative
 *   for "is this favorited?" — toggling the heart never affects folder content
 *   (except `remove()` which retires a handle from every folder for consistency).
 *
 * State shape (in-memory, localStorage, and Worker payload):
 *   {
 *     handles: string[],
 *     folders: [{ id: string, name: string, color: string, handles: string[] }]
 *   }
 *
 * Closed color palette:
 *   red, orange, yellow, green, teal, blue, purple, pink
 *
 * API:
 *   dgFav.list()                          -> string[]   master handle list
 *   dgFav.has(handle)                     -> boolean
 *   dgFav.toggle(handle)                  -> boolean    new wishlisted state
 *   dgFav.remove(handle)                  -> void       removes from master AND every folder
 *   dgFav.onChange(fn)                    -> void       fn(handles, folders)
 *   dgFav.syncFromServer()                -> Promise
 *   dgFav.syncToServer()                  -> Promise
 *
 *   dgFav.folders()                       -> Folder[]
 *   dgFav.foldersOf(handle)               -> string[]   folder ids containing this handle
 *   dgFav.createFolder(name, color?)      -> Folder
 *   dgFav.renameFolder(id, name)          -> boolean
 *   dgFav.setFolderColor(id, color)       -> boolean
 *   dgFav.deleteFolder(id)                -> boolean    folder gone, handles preserved
 *   dgFav.addToFolder(folderId, handle)   -> boolean
 *   dgFav.removeFromFolder(folderId, h)   -> boolean
 *   dgFav.colors()                        -> string[]   available palette
 *
 *   dgFav.isSyncEnabled()                 -> boolean
 *
 * Sync model (v5.2):
 *   • Pull: server replaces local unless local has unsynced edits / pending deletions.
 *   • Push: full state replace on server; GET verify after POST (retry if mismatch).
 *   • Bootstrap: server empty + local has data → push once.
 *
 * Config (injected by theme.liquid):
 *   window.DG_SYNC_CONFIG = {
 *     isLoggedIn: true, appProxyPath: "/apps/favoris"
 *   }
 */
(function () {
    'use strict';

    var CONFIG = window.DG_SYNC_CONFIG || {};
    var CUSTOMER_ID = CONFIG.customerId || null;
    var STORAGE_KEY = CUSTOMER_ID ? ('dg:fav:' + CUSTOMER_ID) : 'dg:fav';
    var APP_PROXY = (CONFIG.appProxyPath || '/apps/favoris').replace(/\/+$/, '');
    var IS_LOGGED_IN = !!(CONFIG.isLoggedIn && APP_PROXY);

    var ALLOWED_COLORS = ['red', 'orange', 'yellow', 'green', 'teal', 'blue', 'purple', 'pink'];
    var DEFAULT_COLOR = 'blue';

    var MAX_HANDLES = 500;
    var MAX_FOLDERS = 50;
    var MAX_FOLDER_NAME = 40;

    var _listeners = [];
    var _syncStatusListeners = [];
    var _syncing = false;
    var _syncPromise = null;
    var _postQueue = Promise.resolve();
    var _lastWriteAt = 0;
    var _lastServerPullAt = 0;
    var _pendingPush = false;
    var _syncGeneration = 0;
    var _syncStatus = IS_LOGGED_IN ? 'pending' : 'guest';
    var _serverEverLoaded = false;

    // ── State helpers ─────────────────────────────────────────────────────────

    function emptyState() {
        return { handles: [], folders: [] };
    }

    function isValidColor(c) {
        return typeof c === 'string' && ALLOWED_COLORS.indexOf(c) !== -1;
    }

    /** Coerce any input into a clean state object {handles, folders}. */
    function normalize(input) {
        if (!input) return emptyState();

        // Legacy: plain array of handles.
        if (Array.isArray(input)) {
            return { handles: cleanHandleList(input), folders: [] };
        }

        if (typeof input !== 'object') return emptyState();

        var handles = cleanHandleList(input.handles);
        var masterSet = {};
        for (var i = 0; i < handles.length; i++) masterSet[handles[i]] = true;

        var folders = [];
        var seenIds = {};
        var rawFolders = Array.isArray(input.folders) ? input.folders : [];
        for (var j = 0; j < rawFolders.length && folders.length < MAX_FOLDERS; j++) {
            var f = rawFolders[j];
            if (!f || typeof f !== 'object') continue;
            var id = typeof f.id === 'string' ? f.id.trim() : '';
            var name = typeof f.name === 'string' ? f.name.trim() : '';
            var color = isValidColor(f.color) ? f.color : DEFAULT_COLOR;
            if (!id || seenIds[id]) continue;
            if (!name) continue;
            if (name.length > MAX_FOLDER_NAME) name = name.slice(0, MAX_FOLDER_NAME);

            var fhRaw = Array.isArray(f.handles) ? f.handles : [];
            var fhSeen = {};
            var fh = [];
            for (var k = 0; k < fhRaw.length; k++) {
                var h = fhRaw[k];
                if (typeof h !== 'string' || !h) continue;
                if (!masterSet[h]) continue; // must be subset of master
                if (fhSeen[h]) continue;
                fhSeen[h] = true;
                fh.push(h);
            }

            seenIds[id] = true;
            folders.push({ id: id, name: name, color: color, handles: fh });
        }

        return { handles: handles, folders: folders };
    }

    function cleanHandleList(arr) {
        if (!Array.isArray(arr)) return [];
        var out = [];
        var seen = {};
        for (var i = 0; i < arr.length && out.length < MAX_HANDLES; i++) {
            var h = arr[i];
            if (typeof h !== 'string' || !h) continue;
            if (h.length > 200) continue;
            if (seen[h]) continue;
            seen[h] = true;
            out.push(h);
        }
        return out;
    }

    function genFolderId() {
        var rnd = Math.random().toString(36).slice(2, 8);
        var stamp = Date.now().toString(36).slice(-4);
        return 'f_' + stamp + rnd;
    }

    // ── localStorage helpers ──────────────────────────────────────────────────

    function readLocal() {
        try {
            var raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return emptyState();
            var parsed = JSON.parse(raw);
            return normalize(parsed);
        } catch (e) { return emptyState(); }
    }

    function writeLocal(state) {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } catch (e) { /* quota exceeded or unavailable */ }
    }

    // ── Migration: copy old keys into new key (one-time) ──────────────────────

    function migrateOldKeys() {
        try {
            var current = readLocal();
            if (current.handles.length > 0 || current.folders.length > 0) return;

            if (CUSTOMER_ID) {
                var generic = localStorage.getItem('dg:fav');
                if (generic) {
                    try {
                        var g = normalize(JSON.parse(generic));
                        if (g.handles.length > 0 || g.folders.length > 0) {
                            writeLocal(g);
                            console.debug('[dgFav] Migrated', g.handles.length, 'favorites from dg:fav →', STORAGE_KEY);
                            return;
                        }
                    } catch (e) { /* ignore */ }
                }
                var custLegacy = 'dg:favorites:' + CUSTOMER_ID;
                var rawCust = localStorage.getItem(custLegacy);
                if (rawCust) {
                    try {
                        var c = normalize(JSON.parse(rawCust));
                        if (c.handles.length > 0) {
                            writeLocal(c);
                            console.debug('[dgFav] Migrated from', custLegacy);
                            return;
                        }
                    } catch (e) { /* ignore */ }
                }
            }

            var legacyKeys = ['dg-favorites', 'dgFavorites', 'dg_favorites'];
            for (var i = 0; i < legacyKeys.length; i++) {
                var raw = localStorage.getItem(legacyKeys[i]);
                if (!raw) continue;
                try {
                    var parsed = JSON.parse(raw);
                    var migrated = normalize(parsed);
                    if (migrated.handles.length > 0) {
                        writeLocal(migrated);
                        console.debug('[dgFav] Migrated', migrated.handles.length, 'favorites from', legacyKeys[i]);
                        break;
                    }
                } catch (e) { /* ignore parse error */ }
            }
        } catch (e) { /* ignore migration errors */ }
    }

    function setSyncStatus(status) {
        _syncStatus = status;
        for (var i = 0; i < _syncStatusListeners.length; i++) {
            try { _syncStatusListeners[i](status); } catch (e) { /* ignore */ }
        }
    }

    function statesEqual(a, b) {
        a = normalize(a);
        b = normalize(b);
        if (a.handles.length !== b.handles.length || a.folders.length !== b.folders.length) return false;
        for (var i = 0; i < a.handles.length; i++) {
            if (b.handles.indexOf(a.handles[i]) === -1) return false;
        }
        return JSON.stringify(a.folders) === JSON.stringify(b.folders);
    }

    /** True when local was edited after the last successful server pull or push. */
    function hasUnsyncedLocalWrites() {
        return _pendingPush || _lastWriteAt > _lastServerPullAt;
    }

    /** Server still lists handles the user removed locally since last ack. */
    function localHasPendingDeletions(localState, serverState) {
        if (_lastWriteAt <= _lastServerPullAt) return false;
        var local = normalize(localState);
        var server = normalize(serverState);
        for (var i = 0; i < server.handles.length; i++) {
            if (local.handles.indexOf(server.handles[i]) === -1) return true;
        }
        return false;
    }

    function markServerAcknowledged() {
        _lastServerPullAt = Date.now();
    }

    function pushPayloadFromLocal() {
        var s = readLocal();
        return {
            handles: s.handles,
            folders: s.folders
        };
    }

    function verifyServerMatchesLocal(attempt) {
        return workerFetch('GET').then(function (data) {
            if (!data) return false;
            var server = normalize({
                handles: data.handles,
                folders: data.folders
            });
            var local = readLocal();
            if (statesEqual(server, local)) {
                markServerAcknowledged();
                console.debug('[dgFav] Server confirmed:', local.handles.length, 'fav');
                return true;
            }
            if (attempt < 3) {
                console.warn('[dgFav] Server mismatch after POST — retry', attempt + 1);
                return workerFetch('POST', pushPayloadFromLocal()).then(function () {
                    return verifyServerMatchesLocal(attempt + 1);
                });
            }
            console.error('[dgFav] Server did not persist deletions — update favoris-dg worker (full replace on POST)');
            return false;
        });
    }

    // ── Notify listeners ──────────────────────────────────────────────────────

    function notifyChange() {
        var state = readLocal();
        for (var i = 0; i < _listeners.length; i++) {
            try { _listeners[i](state.handles, state.folders); } catch (e) { /* listener error */ }
        }
    }

    // ── Worker communication ──────────────────────────────────────────────────

    function workerFetch(method, body) {
        if (!IS_LOGGED_IN) return Promise.resolve(null);

        var opts = {
            method: method,
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json'
            }
        };

        var url = APP_PROXY;
        if (method === 'GET') {
            url += (url.indexOf('?') >= 0 ? '&' : '?') + '_t=' + Date.now();
        } else {
            opts.body = JSON.stringify(body);
        }

        return fetch(url, opts)
            .then(function (res) {
                if (!res.ok) {
                    console.warn('[dgFav] Worker HTTP', res.status);
                    return null;
                }
                return res.json();
            })
            .catch(function (err) {
                console.warn('[dgFav] Worker error:', err.message);
                return null;
            });
    }

    function enqueueServerWrite() {
        _pendingPush = true;
        _postQueue = _postQueue
            .catch(function () {
                // Keep the queue alive after a failed write.
            })
            .then(function () {
                var s = readLocal();
                return workerFetch('POST', pushPayloadFromLocal()).then(function (ok) {
                    if (ok === null) return;
                    console.debug('[dgFav] POST sent:', s.handles.length, 'fav,', s.folders.length, 'folders');
                    return verifyServerMatchesLocal(0);
                });
            })
            .finally(function () {
                _pendingPush = false;
            });
        return _postQueue;
    }

    /**
     * Push the latest local state to the server. Waits for any in-flight
     * GET sync to settle first, then pushes the freshest local snapshot.
     */
    function pushLocalToServer() {
        if (!IS_LOGGED_IN) return Promise.resolve();
        var doSync = enqueueServerWrite;
        if (_syncPromise) {
            return _syncPromise.then(doSync);
        }
        return doSync();
    }

    /**
     * Mutate local state via mutator(state) -> mutated state, then write,
     * mark _lastWriteAt, notify listeners, push to server.
     */
    function mutate(mutator) {
        var s = readLocal();
        var next = mutator(s);
        if (!next) return false;
        writeLocal(next);
        _lastWriteAt = Date.now();
        notifyChange();
        pushLocalToServer();
        return true;
    }

    // ══════════════════════════════════════════════════════════════════════════
    // PUBLIC API
    // ══════════════════════════════════════════════════════════════════════════

    window.dgFav = {

        list: function () {
            return readLocal().handles;
        },

        has: function (handle) {
            if (!handle) return false;
            return readLocal().handles.indexOf(handle) !== -1;
        },

        toggle: function (handle) {
            if (!handle) return false;
            var state = readLocal();
            var idx = state.handles.indexOf(handle);
            var nowWishlisted;
            if (idx === -1) {
                if (state.handles.length >= MAX_HANDLES) {
                    console.warn('[dgFav] Max favorites reached:', MAX_HANDLES);
                    return false;
                }
                state.handles.push(handle);
                nowWishlisted = true;
            } else {
                state.handles.splice(idx, 1);
                // Cascade: remove from every folder.
                for (var i = 0; i < state.folders.length; i++) {
                    var fh = state.folders[i].handles;
                    var fIdx = fh.indexOf(handle);
                    if (fIdx !== -1) fh.splice(fIdx, 1);
                }
                nowWishlisted = false;
            }
            writeLocal(state);
            _lastWriteAt = Date.now();
            notifyChange();
            pushLocalToServer();
            return nowWishlisted;
        },

        remove: function (handle) {
            if (!handle) return;
            mutate(function (state) {
                var idx = state.handles.indexOf(handle);
                if (idx === -1) return null;
                state.handles.splice(idx, 1);
                for (var i = 0; i < state.folders.length; i++) {
                    var fh = state.folders[i].handles;
                    var fIdx = fh.indexOf(handle);
                    if (fIdx !== -1) fh.splice(fIdx, 1);
                }
                return state;
            });
        },

        /** Retire des favoris les handles dont le produit n'existe plus (un seul sync). */
        pruneHandles: function (handles) {
            if (!Array.isArray(handles) || !handles.length) return 0;
            var removed = 0;
            var ok = mutate(function (state) {
                for (var i = 0; i < handles.length; i++) {
                    var h = handles[i];
                    if (typeof h !== 'string' || !h) continue;
                    var idx = state.handles.indexOf(h);
                    if (idx === -1) continue;
                    state.handles.splice(idx, 1);
                    removed++;
                    for (var f = 0; f < state.folders.length; f++) {
                        var fh = state.folders[f].handles;
                        var fIdx = fh.indexOf(h);
                        if (fIdx !== -1) fh.splice(fIdx, 1);
                    }
                }
                return removed > 0 ? state : null;
            });
            return ok ? removed : 0;
        },

        onChange: function (fn) {
            if (typeof fn === 'function') _listeners.push(fn);
        },

        // ── Folders ───────────────────────────────────────────────────────────

        folders: function () {
            return readLocal().folders.map(function (f) {
                return { id: f.id, name: f.name, color: f.color, handles: f.handles.slice() };
            });
        },

        foldersOf: function (handle) {
            if (!handle) return [];
            var state = readLocal();
            var ids = [];
            for (var i = 0; i < state.folders.length; i++) {
                if (state.folders[i].handles.indexOf(handle) !== -1) {
                    ids.push(state.folders[i].id);
                }
            }
            return ids;
        },

        colors: function () {
            return ALLOWED_COLORS.slice();
        },

        createFolder: function (name, color) {
            name = (typeof name === 'string' ? name : '').trim();
            if (!name) return null;
            if (name.length > MAX_FOLDER_NAME) name = name.slice(0, MAX_FOLDER_NAME);
            color = isValidColor(color) ? color : DEFAULT_COLOR;

            var state = readLocal();
            if (state.folders.length >= MAX_FOLDERS) {
                console.warn('[dgFav] Max folders reached:', MAX_FOLDERS);
                return null;
            }
            var id = genFolderId();
            var folder = { id: id, name: name, color: color, handles: [] };
            state.folders.push(folder);
            writeLocal(state);
            _lastWriteAt = Date.now();
            notifyChange();
            pushLocalToServer();
            return { id: folder.id, name: folder.name, color: folder.color, handles: [] };
        },

        renameFolder: function (id, name) {
            if (!id) return false;
            name = (typeof name === 'string' ? name : '').trim();
            if (!name) return false;
            if (name.length > MAX_FOLDER_NAME) name = name.slice(0, MAX_FOLDER_NAME);
            return mutate(function (state) {
                for (var i = 0; i < state.folders.length; i++) {
                    if (state.folders[i].id === id) {
                        state.folders[i].name = name;
                        return state;
                    }
                }
                return null;
            });
        },

        setFolderColor: function (id, color) {
            if (!id || !isValidColor(color)) return false;
            return mutate(function (state) {
                for (var i = 0; i < state.folders.length; i++) {
                    if (state.folders[i].id === id) {
                        state.folders[i].color = color;
                        return state;
                    }
                }
                return null;
            });
        },

        deleteFolder: function (id) {
            if (!id) return false;
            return mutate(function (state) {
                for (var i = 0; i < state.folders.length; i++) {
                    if (state.folders[i].id === id) {
                        state.folders.splice(i, 1);
                        return state;
                    }
                }
                return null;
            });
        },

        addToFolder: function (folderId, handle) {
            if (!folderId || !handle) return false;
            return mutate(function (state) {
                if (state.handles.indexOf(handle) === -1) return null; // not a favorite
                for (var i = 0; i < state.folders.length; i++) {
                    if (state.folders[i].id === folderId) {
                        if (state.folders[i].handles.indexOf(handle) !== -1) return null; // already in
                        state.folders[i].handles.push(handle);
                        return state;
                    }
                }
                return null;
            });
        },

        removeFromFolder: function (folderId, handle) {
            if (!folderId || !handle) return false;
            return mutate(function (state) {
                for (var i = 0; i < state.folders.length; i++) {
                    if (state.folders[i].id === folderId) {
                        var idx = state.folders[i].handles.indexOf(handle);
                        if (idx === -1) return null;
                        state.folders[i].handles.splice(idx, 1);
                        return state;
                    }
                }
                return null;
            });
        },

        // ── Sync ──────────────────────────────────────────────────────────────

        syncFromServer: function () {
            if (!IS_LOGGED_IN) {
                setSyncStatus('guest');
                return Promise.resolve();
            }
            if (_syncPromise) return _syncPromise;

            _syncing = true;
            setSyncStatus('pending');
            var syncGeneration = ++_syncGeneration;

            function fetchWithRetry(attempt) {
                return workerFetch('GET').then(function (data) {
                    if (data) return data;
                    if (attempt >= 2) return null;
                    return new Promise(function (resolve) {
                        setTimeout(function () {
                            resolve(fetchWithRetry(attempt + 1));
                        }, 400 * attempt);
                    });
                });
            }

            _syncPromise = fetchWithRetry(1)
                .then(function (data) {
                    if (syncGeneration !== _syncGeneration) return;

                    if (hasUnsyncedLocalWrites()) {
                        console.debug('[dgFav] Local changes pending — pushing to server');
                        setSyncStatus('ok');
                        return enqueueServerWrite();
                    }

                    var local = readLocal();

                    if (!data) {
                        console.warn('[dgFav] Sync failed — keeping local, will retry');
                        setSyncStatus('error');
                        return;
                    }

                    var server = normalize({
                        handles: data.handles,
                        folders: data.folders
                    });
                    _serverEverLoaded = true;

                    if (localHasPendingDeletions(local, server)) {
                        console.debug('[dgFav] Pending deletions on server — forcing push');
                        setSyncStatus('ok');
                        return enqueueServerWrite();
                    }

                    // First-time bootstrap: server empty, local has favorites → upload local.
                    var serverEmpty = server.handles.length === 0 && server.folders.length === 0;
                    var localHasData = local.handles.length > 0 || local.folders.length > 0;
                    if (serverEmpty && localHasData) {
                        console.debug('[dgFav] Bootstrapping server from local');
                        setSyncStatus('ok');
                        return enqueueServerWrite();
                    }

                    // Pull: server is source of truth (fixes cross-device ghost re-adds).
                    if (!statesEqual(server, local)) {
                        writeLocal(server);
                        markServerAcknowledged();
                        console.debug('[dgFav] Synced from server:',
                            server.handles.length, 'fav,',
                            server.folders.length, 'folders');
                        notifyChange();
                    } else {
                        markServerAcknowledged();
                        console.debug('[dgFav] Synced (already up to date)');
                    }

                    setSyncStatus('ok');
                })
                .catch(function (err) {
                    console.warn('[dgFav] Sync error:', err.message || err);
                    setSyncStatus('error');
                })
                .then(function () {
                    _syncing = false;
                    _syncPromise = null;
                });

            return _syncPromise;
        },

        getSyncStatus: function () {
            return _syncStatus;
        },

        onSyncStatus: function (fn) {
            if (typeof fn === 'function') _syncStatusListeners.push(fn);
        },

        syncToServer: function () {
            if (!IS_LOGGED_IN) return Promise.resolve();
            var s = readLocal();
            return enqueueServerWrite().then(function () {
                console.debug('[dgFav] Pushed', s.handles.length, 'fav /', s.folders.length, 'folders');
            });
        },

        isSyncEnabled: function () {
            return IS_LOGGED_IN;
        }
    };

    // ── Auto-init ─────────────────────────────────────────────────────────────

    migrateOldKeys();

    if (IS_LOGGED_IN) {
        window.dgFav.syncFromServer();
        console.debug('[dgFav] Init: logged in — sync via', APP_PROXY, 'storage', STORAGE_KEY);

        document.addEventListener('visibilitychange', function () {
            if (document.visibilityState === 'visible') {
                window.dgFav.syncFromServer();
            }
        });
        window.addEventListener('pageshow', function (e) {
            if (e.persisted) window.dgFav.syncFromServer();
        });
    } else {
        setSyncStatus('guest');
        console.debug('[dgFav] Init: guest mode (localStorage only)');
    }

})();
